import { Grid, InputAdornment } from '@mui/material';

import { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ITabValues } from 'common/api/type-def';
import { ReactComponent as SearchOutlined } from 'common/assets/images/search-outline.svg';
import TabBar, { ITabPanel } from 'common/components/TabBar/TabBar';
import TextFieldComponent from 'common/components/TextField/TextField';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { API_FAIL_ERROR_MESSAGE } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import {
	setMyApprovalsSelectedTabIndex,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import { formatNumber } from 'common/utils/utilFunctions';

import { getMyApprovalTableData } from '../api';
import { IMyApprovalsTableData } from '../api/type-def';

import MyApprovalsTable from './MyApprovalsTable';
import {
	defaultTabValues,
	IMyApprovalTabsData,
	REQUEST_CHANGE_API_KEY,
	TAB_NAMES,
} from './MyApprovalUtils';

import classes from './MyApprovals.module.scss';

const { OK, NOT_FOUND } = HTTP_STATUS_CODES;

const MyApprovals = () => {
	const dispatch = useDispatch();

	const { PENDING_TAB, APPROVED_TAB, REJECTED_TAB } = TAB_NAMES;
	const [searchText, setSearchText] = useState<string>('');
	const [tabValues, setTabValues] =
		useState<IMyApprovalTabsData>(defaultTabValues);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const defaultPageQueryParams = { pageNumber: 0, pageSize: 10 };

	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);
	const activeTabIndex = useSelector(
		(state: RootState) => state.common.myApprovalsSelectedTabIndex,
	);

	const myApprovalTabs: ITabValues[] = [
		{
			name: PENDING_TAB,
			count: formatNumber(tabValues.pendingTab.count),
		},
		{
			name: APPROVED_TAB,
			count: formatNumber(tabValues.approvedTab.count),
		},
		{
			name: REJECTED_TAB,
			count: formatNumber(tabValues.rejectedTab.count),
		},
	];

	const handleAPISuccessResponse = (
		data: IMyApprovalsTableData,
		tabName: string,
	) => {
		const { content, totalCount } = data;
		switch (tabName) {
			case PENDING_TAB:
				setTabValues((prev) => ({
					...prev,
					pendingTab: { ...prev.pendingTab, data: content, count: totalCount },
				}));
				break;
			case APPROVED_TAB:
				setTabValues((prev) => ({
					...prev,
					approvedTab: {
						...prev.approvedTab,
						data: content,
						count: totalCount,
					},
				}));
				break;
			case REJECTED_TAB:
				setTabValues((prev) => ({
					...prev,
					rejectedTab: {
						...prev.rejectedTab,
						data: content,
						count: totalCount,
					},
				}));
				break;
			default:
				break;
		}
	};

	const getTableData = async (data: any, tabName: string) => {
		setIsLoading(true);
		const tabArray = [];
		tabArray.push(tabName);
		if (tabName === PENDING_TAB) {
			tabArray.push(REQUEST_CHANGE_API_KEY);
		}
		const response = await getMyApprovalTableData(data, searchText, tabArray);
		if (response.statusCode === OK && response.responseBody) {
			handleAPISuccessResponse(response.responseBody, tabName);
		} else if (response.statusCode === NOT_FOUND) {
			setTabValues(defaultTabValues);
		} else {
			dispatch(
				setToastMessage({
					isToastOpen: true,
					message: API_FAIL_ERROR_MESSAGE,
					severity: 'error',
				}),
			);
		}
		setIsLoading(false);
	};

	const getPendingTabData = async (data: any) => {
		getTableData(data, PENDING_TAB);
	};

	const getApprovedTabData = async (data: any) => {
		getTableData(data, APPROVED_TAB);
	};

	const getRejectedTabData = async (data: any) => {
		getTableData(data, REJECTED_TAB);
	};

	const getAllTabsData = async (data?: any) => {
		getPendingTabData(data || defaultPageQueryParams);
		getApprovedTabData(data || defaultPageQueryParams);
		getRejectedTabData(data || defaultPageQueryParams);
	};

	useEffect(() => {
		getAllTabsData();
	}, [searchText]);

	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				isToastOpen: false,
				message: '',
				severity: '',
			}),
		);
	}, []);

	const myApprovalTabComponents: ITabPanel[] = [
		{
			id: 1,
			children: (
				<MyApprovalsTable
					isLoading={isLoading}
					id="MyApprovalsTable"
					data={tabValues.pendingTab.data}
					pageCount={tabValues.pendingTab.count}
					fetchData={getAllTabsData}
				/>
			),
		},
		{
			id: 2,
			children: (
				<MyApprovalsTable
					isLoading={isLoading}
					id="MyApprovalsTable"
					data={tabValues.approvedTab.data}
					pageCount={tabValues.approvedTab.count}
					fetchData={getApprovedTabData}
				/>
			),
		},
		{
			id: 3,
			children: (
				<MyApprovalsTable
					isLoading={isLoading}
					id="MyApprovalsTable"
					data={tabValues.rejectedTab.data}
					pageCount={tabValues.rejectedTab.count}
					fetchData={getRejectedTabData}
				/>
			),
		},
	];

	const handleMyApprovalTabChange = (index: number) => {
		dispatch(setMyApprovalsSelectedTabIndex(index));
	};

	const handleSearch = (data: string) => {
		setSearchText(data);
	};

	const searchField = (
		<TextFieldComponent
			inputProps={{
				startAdornment: (
					<InputAdornment position="start">
						<SearchOutlined className={classes['input-search-icon']} />
					</InputAdornment>
				),
				'data-testid': 'searchInput',
			}}
			fullWidth={false}
			type="text"
			name="Search Field"
			value={searchText}
			onChange={(e) => handleSearch(e?.target?.value)}
			className={classes['search-input']}
			placeholder="Search"
		/>
	);

	return (
		<Grid container display="flex">
			<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
				<TabBar
					testId="myapproval-tab"
					activeTab={activeTabIndex}
					details={myApprovalTabs}
					showCount
					childComponent={searchField}
					components={myApprovalTabComponents}
					showButton={false}
					onTabChange={handleMyApprovalTabChange}
				/>
			</Grid>
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
			/>
		</Grid>
	);
};

export default MyApprovals;
